import React, { useState, useEffect, useMemo, useCallback } from "react";
import debounce from "lodash/debounce";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  InputAdornment,
  Typography,
  Box,
} from "@mui/material";
import { Visibility, Search, Send, Schedule } from "@mui/icons-material";
import {
  RETIREMENT_NOTIFICATION,
  RETIREMENT_EMAIL_SUBJECT,
} from "./constants/notificationTemplates";
import MessagePreviewModal from "./MessagePreviewModal";
import PaginatedReport from "../../../common/components/PaginatedReport";

const NotifyTeachersModal = ({
  open,
  onClose,
  teachers,
  onSendNotification,
  isLoading,
}) => {
  const [message, setMessage] = useState(RETIREMENT_NOTIFICATION);
  const [emailSubject, setEmailSubject] = useState(RETIREMENT_EMAIL_SUBJECT);
  const [selectedTeachers, setSelectedTeachers] = useState({});
  const [selectAll, setSelectAll] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  useEffect(() => {
    if (open) {
      setMessage(RETIREMENT_NOTIFICATION);
      setEmailSubject(RETIREMENT_EMAIL_SUBJECT);
      // setSelectAll(true);
      // const newSelected = {};
      // teachers.forEach((teacher) => {
      //   newSelected[teacher.id] = true;
      // });
      // setSelectedTeachers(newSelected);
    }
  }, [open, teachers]);

  const handleSelectAll = (event) => {
    setSelectAll(event.target.checked);
    const newSelected = {};
    teachers.forEach((teacher) => {
      newSelected[teacher.id] = event.target.checked;
    });
    setSelectedTeachers(newSelected);
  };

  const handleSelectTeacher = (id) => {
    setSelectedTeachers((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const processMessageForTeacher = (teacher, template) => {
    const replacements = {
      firstName: teacher.firstName,
      lastName: teacher.lastName,
      age: teacher.age.toString(),
      school: teacher.schoolName,
    };

    return Object.entries(replacements).reduce(
      (processed, [key, value]) =>
        processed.replace(new RegExp(`\\{${key}\\}`, "g"), value),
      template
    );
  };

  const handleSend = () => {
    const notifications = teachers
      .filter((teacher) => selectedTeachers[teacher.id])
      .map((teacher) => ({
        teacherId: teacher.id,
        email: teacher.email,
        phoneNumber: teacher.phoneNumber,
        subject: processMessageForTeacher(teacher, emailSubject),
        message: processMessageForTeacher(teacher, message),
      }));

    onSendNotification({ notifications });
  };

  const previewMessage = (teacher) => {
    let preview = message;
    let subject = emailSubject;
    const replacements = {
      firstName: teacher.firstName,
      lastName: teacher.lastName,
      age: teacher.age.toString(),
      school: teacher.schoolName,
    };

    Object.entries(replacements).forEach(([key, value]) => {
      preview = preview.replace(new RegExp(`\\{${key}\\}`, "g"), value);
      subject = subject.replace(new RegExp(`\\{${key}\\}`, "g"), value);
    });

    return `Subject: ${subject}\n\n${preview}`;
  };

  const handlePreview = (teacher) => {
    setPreviewContent(previewMessage(teacher));
    setPreviewModalOpen(true);
  };

  const teachersLookup = useMemo(() => {
    const lookup = {};
    teachers.forEach((teacher) => {
      const searchableText =
        `${teacher.firstName} ${teacher.lastName} ${teacher.age} ${teacher.email} ${teacher.phoneNumber}`.toLowerCase();
      lookup[teacher.id] = searchableText;
    });
    return lookup;
  }, [teachers]);

  const debouncedSearch = useCallback(
    debounce((value) => {
      setDebouncedSearchQuery(value.toLowerCase());
    }, 300),
    []
  );

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    debouncedSearch(e.target.value);
  };

  const filteredTeachers = useMemo(() => {
    if (!debouncedSearchQuery) return teachers;

    return teachers.filter((teacher) =>
      teachersLookup[teacher.id].includes(debouncedSearchQuery)
    );
  }, [teachers, debouncedSearchQuery, teachersLookup]);

  const selectedCount = useMemo(
    () => Object.values(selectedTeachers).filter(Boolean).length,
    [selectedTeachers]
  );

  const renderTeachersTable = (paginatedData) => (
    <Paper elevation={0} sx={{ border: "1px solid #e0e0e0", borderRadius: 1 }}>
      <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder="Search teacher by name, age, email or phone..."
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="action" />
              </InputAdornment>
            ),
          }}
        />
        <Box
          sx={{
            mt: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 1,
          }}
        >
          <Typography variant="body2" color="textSecondary">
            {filteredTeachers.length}{" "}
            {filteredTeachers.length === 1 ? "teacher found" : "teachers found"}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "primary.main",
              bgcolor: "primary.lighter",
              py: 0.5,
              px: 1.5,
              borderRadius: 1,
              fontWeight: 500,
            }}
          >
            {selectedCount} selected for notification
          </Typography>
        </Box>
      </Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ py: 0 }}>
                <FormControlLabel
                  control={
                    <Checkbox checked={selectAll} onChange={handleSelectAll} />
                  }
                />
              </TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Age</TableCell>
              <TableCell>School</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Preview</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((teacher) => (
              <TableRow key={teacher.id}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedTeachers[teacher.id] || false}
                    onChange={() => handleSelectTeacher(teacher.id)}
                  />
                </TableCell>
                <TableCell>
                  {teacher.firstName} {teacher.lastName}
                </TableCell>
                <TableCell>{teacher.age}</TableCell>
                <TableCell>{teacher.schoolName}</TableCell>
                <TableCell>{teacher.phoneNumber}</TableCell>
                <TableCell sx={{ textAlign: "center" }}>
                  <IconButton
                    size="small"
                    onClick={() => handlePreview(teacher)}
                  >
                    <Visibility />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Notify Teachers (65+ years)
        </DialogTitle>
        <DialogContent>
          <div className="my-3">
            <TextField
              fullWidth
              variant="outlined"
              label="Subject"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              helperText="Available variables: {firstName}, {lastName}, {age}, {school}"
            />
          </div>

          <div className="mb-3">
            <TextField
              fullWidth
              multiline
              rows={10}
              variant="outlined"
              label="Body"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              helperText="Available variables: {firstName}, {lastName}, {age}, {school}"
            />
          </div>

          <PaginatedReport
            data={filteredTeachers}
            rowsPerPage={5}
            renderTable={renderTeachersTable}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            variant="contained"
            color="primary"
            disabled={
              !message ||
              !Object.values(selectedTeachers).some((v) => v) ||
              isLoading
            }
            startIcon={isLoading ? <Schedule /> : <Send />}
          >
            {isLoading ? "Sending..." : "Send Notification"}
          </Button>
        </DialogActions>
      </Dialog>
      <MessagePreviewModal
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        message={previewContent}
      />
    </>
  );
};

export default NotifyTeachersModal;
